import { useMemo } from "react";
import { buildPositionFlowGeometry } from "../../lib/flowGeometry.js";

// PositionFlowOverlay — the vertical sankey between stacked Position levels, in
// the same flow language as CompositionFlowOverlay rotated 90°: thin green
// S-curves with a plot-bg halo interleaved PER curve (so they carve at
// crossings), attaching to small horizontal node bars at each row's edge where
// the flows stack proportionally.
//
// The DESCENT SPINE (your selected member → the next level's selected member)
// rides a higher base alpha so the thread of "where you are" reads at rest; the
// sibling CROSS-LINKS sit quiet beneath it. Hovering any cell pops the ribbons
// touching it and dims the rest — the spine floored to base so your path never
// fully disappears while you explore a sibling.
//
// An absolutely-positioned SVG UNDER the rows (z-0): the row labels + pager sit
// above (z-1) and cleanly occlude any ribbon that passes behind them.

const BASE_LINK = 0.18;
const SPINE_LINK = 0.6;
const SEL_LINK = 0.72;
const DIM_LINK = 0.07;
const BASE_NODE = 0.4;
const SEL_NODE = 0.85;
const DIM_NODE = 0.2;

/**
 * @param {number} width
 * @param {number} height
 * @param {Array<{top:number, bottom:number, cells:Array<{id, cx, w, selected, value}>}>} rows  per level, indexed by level
 * @param {Array<{pair, aId, bId, count, spine}>} flows
 * @param {{row:number, id:string}|null} hovered  the focused cell (level idx, member id)
 */
export default function PositionFlowOverlay({ width, height, rows, flows, hovered }) {
  // Geometry lives in flowGeometry.js (React/DOM-free, unit-tested); the memo
  // just avoids recomputing on hover, which only changes alpha.
  const { paths, nodes } = useMemo(
    () => buildPositionFlowGeometry(width, height, rows, flows),
    [width, height, rows, flows],
  );

  if (!paths.length) return null;

  const hasFocus = hovered != null && hovered.id != null && hovered.row != null;
  // Only defined under focus, so it never dereferences a null `hovered`.
  const touches = hasFocus
    ? (p) => (p.pair === hovered.row && p.aId === String(hovered.id)) || (p.pair + 1 === hovered.row && p.bId === String(hovered.id))
    : null;
  const lit = new Set();
  if (hasFocus) for (const p of paths) if (touches(p)) { lit.add(`${p.pair}:${p.aId}`); lit.add(`${p.pair + 1}:${p.bId}`); }

  const alphaFor = (p) => (hasFocus ? (touches(p) ? SEL_LINK : (p.spine ? BASE_LINK : DIM_LINK)) : (p.spine ? SPINE_LINK : BASE_LINK));
  // Paint quiet first, spine above base, focused last — so the important
  // ribbons carve through the rest (drawFlowLinks' two-pass order).
  const rank = (p) => (hasFocus ? (touches(p) ? 2 : (p.spine ? 1 : 0)) : (p.spine ? 1 : 0));
  const ordered = paths.map((p, i) => ({ p, i })).sort((a, b) => rank(a.p) - rank(b.p));

  return (
    <svg className="pointer-events-none absolute inset-0 z-0" width={width} height={height} style={{ overflow: "visible" }} aria-hidden="true">
      {ordered.map(({ p, i }) => (
        <g key={i}>
          <path d={p.d} fill="none" stroke="var(--plot-bg)" strokeWidth={p.sw + 2.4} strokeLinecap="round" />
          <path d={p.d} fill="none" stroke="var(--green)" strokeOpacity={alphaFor(p)} strokeWidth={p.sw} strokeLinecap="round" />
        </g>
      ))}
      {nodes.map((n, i) => {
        const key = `${n.row}:${n.id}`;
        const highlighted = hasFocus && n.row === hovered.row && String(n.id) === String(hovered.id);
        const alpha = hasFocus ? (highlighted ? SEL_NODE : (lit.has(key) ? BASE_NODE : DIM_NODE)) : BASE_NODE;
        return <rect key={`n${i}`} x={n.x} y={n.y} width={n.w} height={n.h} rx="1" fill="var(--green)" fillOpacity={alpha} />;
      })}
    </svg>
  );
}
